import React from "react"
import PropTypes from "prop-types"
import { Box, Skeleton, Typography } from "@mui/material"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import theme from "src/v2/theme/theme"

export const memoriaCalculoTableSx = {
    border: "none",
    backgroundColor: "transparent",
    color: theme.palette.common.white,
    height: "100%",

    "& .MuiDataGrid-virtualScroller": {
        overflowX: "auto !important",
        overflowY: "auto !important",
        scrollbarGutter: "stable",
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(217, 217, 217, 0.65) transparent",
    },

    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
        width: "10px",
        height: "10px",
    },

    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(217, 217, 217, 0.65)",
        border: "2px solid transparent",
        borderRadius: "8px",
        backgroundClip: "padding-box",
    },

    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track, & .MuiDataGrid-virtualScroller::-webkit-scrollbar-corner": {
        background: "transparent",
    },

    "& .MuiDataGrid-root": {
        border: "none",
        backgroundColor: "transparent",
        color: theme.palette.common.white,
        height: "100%",
        fontFamily: theme.typography.fontFamily,
    },

    "& .MuiDataGrid-main": {
        backgroundColor: "transparent",
    },

    "& .MuiDataGrid-withBorderColor": {
        borderColor: "transparent",
    },

    "& .MuiDataGrid-columnHeaders": {
        borderBottom: "none",
        backgroundColor: "transparent",
        minHeight: "56px !important",
        maxHeight: "56px !important",
        lineHeight: "56px !important",
    },

    "& .MuiDataGrid-columnHeader": {
        borderRight: "none",
        outline: "none !important",
        backgroundColor: "transparent",
    },

    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
        outline: "none",
    },

    "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitleContainerContent *": {
        color: `${theme.palette.common.white} !important`,
        fontSize: "14px !important",
        fontWeight: 700,
        textTransform: "uppercase",
        lineHeight: "1.2 !important",
        whiteSpace: "normal",
    },

    "& .MuiDataGrid-cell": {
        borderBottom: "none",
        borderRight: "none",
        color: `${theme.palette.common.white} !important`,
        fontSize: "14px !important",
        lineHeight: "1.35 !important",
        display: "flex",
        alignItems: "center",
        outline: "none !important",
    },

    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
        outline: "none",
    },

    "& .MuiDataGrid-row": {
        borderBottom: "none",
        minHeight: "56px !important",
    },

    "& .MuiDataGrid-virtualScrollerRenderZone .MuiDataGrid-row:nth-of-type(odd)": {
        backgroundColor: "rgba(255, 255, 255, 0.03)",
    },

    "& .MuiDataGrid-virtualScrollerRenderZone .MuiDataGrid-row:nth-of-type(even)": {
        backgroundColor: "#3A4056",
    },

    "& .MuiDataGrid-row:hover": {
        backgroundColor: "#444B63",
    },

    "& .MuiDataGrid-footerContainer": {
        borderTop: "none",
        color: theme.palette.common.white,
    },

    "& .MuiDataGrid-iconSeparator": {
        display: "none",
    },

    "& .MuiDataGrid-menuIconButton, & .MuiDataGrid-sortIcon, & .MuiDataGrid-filterIcon": {
        color: theme.palette.common.white,
    },

    "& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
        color: theme.palette.common.white,
    },
}

export const SectionTitle = ({ children, sx }) => {
    if (!children) return null

    return (
        <Typography
            sx={{
                mb: 2,
                fontSize: "18px !important",
                fontWeight: 400,
                color: theme.palette.common.white,
                ...sx,
            }}
        >
            {children}
        </Typography>
    )
}

SectionTitle.propTypes = {
    children: PropTypes.node,
    sx: PropTypes.object,
}

const getFieldGridColumn = (field) => ({
    xxxs: `span ${field.xxxs ?? field.xs ?? 6}`,
    xs: `span ${field.xs ?? 6}`,
    sm: `span ${field.sm ?? 6}`,
    md: `span ${field.md ?? 3}`,
})

const renderValue = (value) => {
    if (value === null || value === undefined || value === "") return "-"
    if (typeof value === "object") return JSON.stringify(value, null, 2)
    return value
}

const FieldBox = ({ label, value }) => (
    <Box
        sx={{
            pl: 1.5,
            borderLeft: `1px solid ${theme.palette.input.borderColor.white70}`,
            width: "100%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
        }}
    >
        <Typography
            variant="contraHeading"
            sx={{
                color: theme.palette.primary.white70,
                mb: 1,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
            }}
        >
            {label}
        </Typography>

        <Typography
            component="span"
            variant="body2"
            sx={{
                color: theme.palette.common.white,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                whiteSpace: "pre-line",
            }}
        >
            {renderValue(value)}
        </Typography>
    </Box>
)

FieldBox.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
}

const skeletonSx = {
    bgcolor: "rgba(255, 255, 255, 0.1)",
    "&::after": {
        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)",
    },
}

const FieldsSkeleton = () => (
    <Box
        aria-label="Carregando parâmetros"
        sx={{
            display: "grid",
            gridTemplateColumns: {
                xxxs: "repeat(1, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 3,
        }}
    >
        {Array.from({ length: 8 }).map((_, index) => (
            <Box key={index} sx={{ pl: 1.5, borderLeft: `1px solid ${theme.palette.input.borderColor.white70}` }}>
                <Skeleton animation="wave" width="55%" height={18} sx={skeletonSx} />
                <Skeleton animation="wave" width="85%" height={24} sx={skeletonSx} />
            </Box>
        ))}
    </Box>
)

export const MemoriaCalculoFieldsSection = ({ title, sections = [], loading }) => {
    return (
        <Box>
            <SectionTitle>{title}</SectionTitle>

            {loading ? <FieldsSkeleton /> : sections.map((section, sectionIndex) => (
                <Box
                    key={`fields-row-${sectionIndex}`}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
                        columnGap: 2,
                        rowGap: 2,
                        mb: sectionIndex < sections.length - 1 ? 3 : 0,
                        minWidth: 0,
                    }}
                >
                    {section.fields.map((field) => (
                        <Box
                            key={field.label}
                            sx={{
                                gridColumn: getFieldGridColumn(field),
                                minWidth: 0,
                                display: "flex",
                            }}
                        >
                            <FieldBox label={field.label} value={field.value} />
                        </Box>
                    ))}
                </Box>
            ))}
        </Box>
    )
}

MemoriaCalculoFieldsSection.propTypes = {
    title: PropTypes.string,
    sections: PropTypes.array,
    loading: PropTypes.bool,
}

const getColumnField = (column) => column.field ?? column.key
const getColumnHeaderName = (column) => column.headerName ?? column.label

const buildTableColumns = (columns = []) =>
    columns.map((column) => ({
        ...column,
        field: getColumnField(column),
        headerName: getColumnHeaderName(column),
        flex: column.flex ?? 1,
        minWidth: column.minWidth ?? 120,
        sortable: column.sortable ?? true,
        filterable: column.filterable ?? true,
        renderCell: ({ value, row }) =>
            column.formatter ? column.formatter(value, row) : renderValue(value),
    }))

const buildTableRows = (rows = []) =>
    rows.map((row, index) => ({
        ...row,
        id: row?.id ?? row?.ID ?? index,
    }))

const TableSkeleton = ({ columns }) => (
    <Box aria-label="Carregando tabela" sx={{ height: 400, overflow: "hidden" }}>
        <Box sx={{ display: "flex", gap: 2, py: 2 }}>
            {columns.slice(0, 6).map((column, index) => (
                <Skeleton
                    key={getColumnField(column) ?? index}
                    animation="wave"
                    height={24}
                    sx={{ ...skeletonSx, flex: 1 }}
                />
            ))}
        </Box>

        {Array.from({ length: 6 }).map((_, rowIndex) => (
            <Box
                key={rowIndex}
                sx={{
                    display: "flex",
                    gap: 2,
                    px: 1,
                    py: 2,
                    bgcolor: rowIndex % 2 ? "#3A4056" : "rgba(255, 255, 255, 0.03)",
                }}
            >
                {columns.slice(0, 6).map((column, columnIndex) => (
                    <Skeleton
                        key={getColumnField(column) ?? columnIndex}
                        animation="wave"
                        height={20}
                        sx={{ ...skeletonSx, flex: 1 }}
                    />
                ))}
            </Box>
        ))}
    </Box>
)

TableSkeleton.propTypes = {
    columns: PropTypes.array.isRequired,
}

export const MemoriaCalculoTableSection = ({ title, columns, rows, loading }) => {
    const gridColumns = buildTableColumns(columns)
    const gridRows = buildTableRows(rows)
    const visibleRowCount = Math.max(1.2, gridRows.length)
    const tableHeight = Math.min(400, 64 + (visibleRowCount * 56))

    return (
        <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
            <SectionTitle>{title}</SectionTitle>

            {loading ? (
                <TableSkeleton columns={columns} />
            ) : (
                <Box
                    sx={{
                        height: tableHeight,
                        maxHeight: 400,
                        width: "100%",
                        minWidth: 0,
                        overflow: "hidden",
                    }}
                >
                    <DataGridTable
                        columns={gridColumns}
                        rows={gridRows}
                        disableDeleteColumn
                        autoRowHeight
                        contained
                        showPagination={false}
                        resetKey={`memoria-calculo-table-${title}`}
                        sx={{
                            ...memoriaCalculoTableSx,
                        }}
                    />
                </Box>
            )}
        </Box>
    )
}

MemoriaCalculoTableSection.propTypes = {
    title: PropTypes.string,
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array,
    loading: PropTypes.bool,
}
